import { Injectable } from '@angular/core';
import { Preferences, PreferencesPlugin } from '@capacitor/preferences';
import { Store } from '@ngrx/store';

import {
  Delivery,
  DeliveryOfflineStatus,
  DownloadStatus,
  OfflineDelivery,
} from '../models/delivery.model';
import { refreshOfflineDelivery, downloadDeliveryImage } from '../store/actions/offline.actions';
import { ToastService } from './toast.service';
import { HelperService } from './helper.service';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  storage: PreferencesPlugin;

  constructor(
    private store: Store<any>,
    private toastService: ToastService,
  ) {
    this.storage = Preferences;
  }

  getDelivery(deliveryKeyCode: string): Promise<Delivery> {
    return this.getDeliveryDetails(deliveryKeyCode);
  }

  async setDelivery(oDelivery: Delivery): Promise<Delivery[]> {
    let deliveries: any;
    const delivery = HelperService.cloneOfflineDelivery(<OfflineDelivery>oDelivery);
    try {
      const data = await this.storage.get({ key: this.storageDeliveryCode });
      deliveries = JSON.parse(data.value) || [];
      let deliveryExist = false;

      deliveries = deliveries.map((deliveryItem: Delivery) => {
        if (deliveryItem.deliveryKeyCode === delivery.deliveryKeyCode) {
          deliveryExist = true;
          return delivery;
        }
        return deliveryItem;
      });

      if (!deliveryExist) {
        delivery.offlineStatus = 'downloading';
        deliveries.push(delivery);
        this.dispatchDeliveryImageLoading(delivery);
        this.setDeliveries(deliveries);
      }
    } catch (error) {
      this.toastService.showErrorInToast('offline-storage.service', 'setDelivery', error);
    }
    return deliveries;
  }

  async updateDeliveryList(delivery): Promise<any> {
    let deliveriesData: any =
      (await this.storage
        .get({key: this.storageDeliveryCode})
        .catch((error: any) =>
          this.toastService.showErrorInToast(
            'offline-storage.service',
            'updateDeliveryList',
            error,
          ),
        ));
    let deliveries = JSON.parse(deliveriesData.value) || [];

    deliveries = deliveries.map((deliveryItem: Delivery) => {
      if (deliveryItem.deliveryKeyCode === delivery.deliveryKeyCode) {
        return delivery;
      }
      return deliveryItem;
    });
    return this.setDeliveries(deliveries);
  }

  saveImgUrl(deliveryKeyCode: string, fieldName: string, localPath: string): Promise<any> {
    return this.getDeliveries().then((deliveryItems: any) => {
      const deliveries = deliveryItems.map((item: Delivery) => {
        if (item.deliveryKeyCode === deliveryKeyCode) {
          item[fieldName] = localPath;
        }
        return item;
      });
      this.setDeliveries(deliveries);
    });
  }

  getDeliveryDetails(deliveryKeyCode: string) {
    return this.getDeliveries()
      .then((deliveries: any) =>
        deliveries.filter((item: Delivery) => item.deliveryKeyCode === deliveryKeyCode),
      )
      .then((deliveries: Delivery[]) => deliveries[0]);
  }

  async getDeliveries(): Promise<any> {
    const deliveries = await this.storage.get({ key: this.storageDeliveryCode });
    return JSON.parse(deliveries.value) ?? Promise.resolve([]);
  }

  setUserKey() {
    if (this.userKey) {
      this.store.dispatch(refreshOfflineDelivery());
    }
  }

  removeAllDeliveries() {
    return this.setDeliveries([]);
  }

  async removeDelivery(deliveryKeyCode: string) {
    const deliveries: any = await this.getDeliveries().catch((err: any) =>
      this.toastService.showErrorInToast('offline-storage.service', 'removeDelivery', err),
    );
    const deliveryIndex = deliveries.findIndex(
      (delivery: Delivery) => delivery.deliveryKeyCode === deliveryKeyCode,
    );
    if (deliveryIndex !== -1) {
      deliveries.splice(deliveryIndex, 1);
      this.setDeliveries(deliveries);
    }

    return deliveries;
  }

  getAudioAssets(deliveryKeyCode) {
    const storageCode = this.getAudioAssetCode(deliveryKeyCode);

    return this.storage.get({key: storageCode});
  }

  getAudioAssetCode(deliveryKeyCode: string): string {
    return `${this.userKey}-assets-audio-${deliveryKeyCode}`;
  }

  getDownloadStatus(downloaded, downloading, enableDownload, cancelDownload): DownloadStatus {
    return {
      downloading,
      downloaded,
      enableDownload,
      cancelDownload,
    };
  }

  async setDeliveryStatus(deliveryKeyCode: string, offlineStatus: DeliveryOfflineStatus) {
    const delivery = await this.getDelivery(deliveryKeyCode);

    if (delivery) {
      delivery.offlineStatus = offlineStatus;

      this.updateDeliveryList(delivery);
    }
  }

  async addEventsParams(params: any): Promise<any> {
    const curParams =
      (await this.getEventsParams().catch((error: any) =>
        this.toastService.showErrorInToast('offline-storage.service', 'getEventsParams', error),
      )) || [];

    curParams.push(params);

    return this.storage.set({key: this.eventStorageKey, value: JSON.stringify(curParams)});
  }

  async addEventsPlayParams(params: any): Promise<any> {
    let curParams =
      (await this.getEventsParams().catch((error: any) =>
        this.toastService.showErrorInToast('offline-storage.service', 'getEventsParams', error),
      )) || [];
    const { assetKeyCode } = params;
    let existParams = false;

    curParams = curParams.map((param: any) => {
      if (param.assetKeyCode === assetKeyCode) {
        param.count = param.count ? param.count + 1 : 2;
        existParams = true;
      }
      return param;
    });

    if (!existParams) {
      curParams.push(params);
    }

    return this.storage.set({key: this.eventStorageKey, value: JSON.stringify(curParams)});
  }

  getEventsParams(): Promise<any> {
    return this.storage.get({key: this.eventStorageKey});
  }

  removeEventsParams(): Promise<any> {
    return this.storage.remove({key: this.eventStorageKey});
  }

  setDeliveries(deliveries) {
    return this.storage
      .set({key: this.storageDeliveryCode, value: JSON.stringify(deliveries)})
      .catch((err: any) =>
        this.toastService.showErrorInToast('offline-storage.service', 'setDeliveries', err),
      );
  }

  private get eventStorageKey(): string {
    return `${this.userKey} - events log`;
  }

  private get storageDeliveryCode(): string {
    return `${this.userKey} - deliveries`;
  }

  private dispatchDeliveryImageLoading(delivery): void {
    this.store.dispatch(
      downloadDeliveryImage({
        url: delivery.deliveryImage,
        fieldName: 'deliveryImage',
        deliveryKeyCode: delivery.deliveryKeyCode,
      }),
    );

    this.store.dispatch(
      downloadDeliveryImage({
        url: delivery.mobileDeliveryImage,
        fieldName: 'mobileDeliveryImage',
        deliveryKeyCode: delivery.deliveryKeyCode,
      }),
    );
  }

  private get userKey(): string {
    return localStorage.getItem('uuid') || '';
  }
}
